// Compact 2026-rates snapshot widget shown in the homepage hero.
//
// Detects the visitor's US state via the /api/geo/state endpoint (IP→state),
// then surfaces today's renovation-loan rates for that state across the four
// main products. CTA → /guides/how-to-finance-a-home-renovation-2026?state=XX
// which pre-fills the full calculator with the detected state.
//
// Design goals:
//   * Tiny visual footprint — fits in the unused right-rail real estate of
//     the hero on lg+ screens without crowding the existing sample-cost card.
//   * Graceful degradation — if geo fails or detects no state, falls back to
//     national-average rates with a "What rates look like nationally" headline.
//   * Single-source-of-truth rates — uses /lib/loanRates.js, the same engine
//     powering the full calculator. Any rate update lives in one file.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, MapPin, ArrowRight } from "lucide-react";
import { effectiveRate, STATE_NAME_BY_CODE } from "@/lib/loanRates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Products to surface — only the 3 most-used, in best-fit order for a 740
// credit score and a $40K loan at the national avg (the most common visitor
// profile per our analytics on financing-intent traffic).
const PRODUCTS = [
  { key: "cash_out_refi", label: "Cash-out refi" },
  { key: "heloc",         label: "HELOC" },
  { key: "personal",      label: "Personal" },
];

export function RateSnapshotWidget() {
  const [state, setState] = useState(null);            // 2-letter or null
  const [stateName, setStateName] = useState(null);    // "California" or null
  const [resolved, setResolved] = useState(false);     // geo lookup completed?

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/geo/state`);
        if (!r.ok) throw new Error("geo lookup failed");
        const data = await r.json();
        if (cancelled) return;
        if (data.state && STATE_NAME_BY_CODE[data.state]) {
          setState(data.state);
          setStateName(data.state_name || STATE_NAME_BY_CODE[data.state]);
        }
      } catch {
        // Silent fail — widget falls back to national averages
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Use detected state if available, otherwise compute national avg (no state
  // adjustment). We pass a placeholder state code that's not in STATE_ADJUST
  // so effectiveRate() returns the base rate without adjustment.
  const displayState = state || "__NATIONAL__";
  const rates = PRODUCTS.map((p) => ({
    ...p,
    rate: effectiveRate(p.key, displayState, 740),
  })).sort((a, b) => a.rate - b.rate);

  const cheapest = rates[0];
  const headline = state ? `Renovation loans in ${stateName}` : "Renovation loan rates today";
  const ctaTarget = `/guides/how-to-finance-a-home-renovation-2026${state ? `?state=${state}` : ""}#calculator`;

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-4 shadow-sm"
      data-testid="rate-snapshot-widget"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-700 text-white">
            <TrendingDown className="h-3.5 w-3.5" />
          </span>
          <p className="font-display text-sm font-semibold text-slate-900" data-testid="rate-snapshot-headline">
            {headline}
          </p>
        </div>
        {state && (
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
            <MapPin className="h-2.5 w-2.5" />
            {state}
          </span>
        )}
      </div>

      <ul className="mt-3 divide-y divide-emerald-100">
        {rates.map((r, i) => (
          <li
            key={r.key}
            className="flex items-center justify-between py-1.5"
            data-testid={`rate-snapshot-row-${r.key}`}
          >
            <span className={`text-xs ${i === 0 ? "font-semibold text-slate-900" : "text-slate-600"}`}>
              {r.label}
              {i === 0 && state && (
                <span className="ml-1.5 rounded-full bg-emerald-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                  best
                </span>
              )}
            </span>
            <span className={`font-mono tabular-nums text-xs ${i === 0 ? "font-semibold text-emerald-800" : "text-slate-700"}`}>
              {r.rate.toFixed(2)}% APR
            </span>
          </li>
        ))}
      </ul>

      <Link
        to={ctaTarget}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
        data-testid="rate-snapshot-cta"
      >
        See your full comparison
        <ArrowRight className="h-3 w-3" />
      </Link>
      <p className="mt-1.5 text-[10px] leading-snug text-slate-500">
        2026 averages · 740 FICO · {state ? `state-adjusted for ${state}` : "national avg"}
        {!resolved && state === null && " · detecting your state…"}
      </p>
    </div>
  );
}
